import { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Input,
  Modal,
  Pagination,
  Select,
  Space,
  Table,
  Typography,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import { EditOutlined } from "@ant-design/icons";

import { useAuth } from "../../auth/model/AuthContext";
import {
  createProposeGoogleSheet,
  createProposeTemplateFromGoogleSheet,
  getProposeTemplate,
  getProposeTemplateErrorMessage,
  listProposeGoogleSheets,
  renameProposeTemplate,
  syncProposeTemplateGoogleSheet,
  updateProposeGoogleSheetPermissions,
  type ProposeGoogleSheet,
  type ProposeGoogleSheetPermission,
  type ProposeGoogleSheetRole,
  type ProposeTemplateFormDefinition,
} from "../api/proposeTemplatesApi";
import { listProposalCoordinatorEmails } from "../api/proposalCoordinatorsApi";

const { Text } = Typography;
const GOOGLE_SHEETS_PAGE_SIZE = 50;
const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const formatDate = (value?: string) =>
  value ? new Date(value).toLocaleString("ja-JP") : "—";

export const GoogleSheetCreateCard = ({
  onCreated,
  onTemplateCreated,
}: {
  onCreated: () => void;
  onTemplateCreated: () => Promise<void>;
}) => {
  const { user } = useAuth();
  const [creating, setCreating] = useState(false);
  const [creatingTemplate, setCreatingTemplate] = useState(false);
  const [guidance, setGuidance] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [templateCreated, setTemplateCreated] = useState(false);
  const [createdSheet, setCreatedSheet] = useState<ProposeGoogleSheet | null>(null);

  const createSheet = async () => {
    const email = user?.email.trim();
    if (!email || !emailPattern.test(email)) {
      setError("ログインユーザーのメールアドレスを取得できません。");
      return;
    }

    setCreating(true);
    setError(null);
    setCreatedSheet(null);
    try {
      const sheet = await createProposeGoogleSheet({ emails: [email] });
      setCreatedSheet(sheet);
      setTemplateCreated(false);
      onCreated();
    } catch (requestError) {
      setError(getProposeTemplateErrorMessage(requestError));
    } finally {
      setCreating(false);
    }
  };

  const createTemplate = async () => {
    if (!createdSheet) return;
    setCreatingTemplate(true);
    setError(null);
    setTemplateCreated(false);
    try {
      await createProposeTemplateFromGoogleSheet({
        spreadsheet_id: createdSheet.id,
        guidance: guidance.trim(),
      });
      setTemplateCreated(true);
      await onTemplateCreated();
    } catch (requestError) {
      setError(getProposeTemplateErrorMessage(requestError));
    } finally {
      setCreatingTemplate(false);
    }
  };

  return (
    <Card title="空のGoogle Sheetsを作成" bordered={false}>
      <Space direction="vertical" size="middle" style={{ width: "100%" }}>
        <Text>
          共有先：{user?.email || "ログインユーザーのメールアドレスを取得できません"}
        </Text>
        {error && <Alert type="error" showIcon message={error} />}
        {createdSheet && (
          <Alert
            type="success"
            showIcon
            message={
              <Space wrap>
                <span>{createdSheet.name} を作成しました。</span>
                <Typography.Link href={createdSheet.url} target="_blank" rel="noreferrer">
                  Google Sheetsを開く
                </Typography.Link>
              </Space>
            }
            description={`共有先: ${(createdSheet.sharedWith ?? []).join(", ") || "—"}`}
          />
        )}
        {createdSheet && (
          <>
            <label htmlFor="google-sheet-template-guidance">Guidance（任意）</label>
            <Input.TextArea
              id="google-sheet-template-guidance"
              value={guidance}
              autoSize={{ minRows: 3, maxRows: 6 }}
              placeholder="テンプレート解析時の指示を入力"
              onChange={(event) => setGuidance(event.target.value)}
            />
            {templateCreated && (
              <Alert type="success" showIcon message="Templateの作成を開始しました。" />
            )}
          </>
        )}
        <div>
          <Space wrap>
            <Button
              type="primary"
              style={{ backgroundColor: "#217346", borderColor: "#217346" }}
              loading={creating}
              disabled={!user?.email.trim() || creatingTemplate}
              onClick={() => void createSheet()}
            >
              作成して開く
            </Button>
            {createdSheet && (
              <Button
                type="primary"
                loading={creatingTemplate}
                disabled={creating}
                onClick={() => void createTemplate()}
              >
                Create Template
              </Button>
            )}
          </Space>
        </div>
      </Space>
    </Card>
  );
};

export const GoogleSheetList = ({
  refreshKey,
  onUpdated,
  onTemplateCreated,
  onTemplateRenamed,
}: {
  refreshKey: number;
  onUpdated: () => void;
  onTemplateCreated: (
    templateId: number,
    form: ProposeTemplateFormDefinition,
  ) => Promise<void>;
  onTemplateRenamed: (templateId: number, name: string, detail: string) => void;
}) => {
  const [items, setItems] = useState<ProposeGoogleSheet[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(GOOGLE_SHEETS_PAGE_SIZE);
  const [total, setTotal] = useState(0);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [permissionSheet, setPermissionSheet] = useState<ProposeGoogleSheet | null>(null);
  const [permissionEmails, setPermissionEmails] = useState<string[]>([]);
  const [permissionRole, setPermissionRole] = useState<ProposeGoogleSheetRole>("edit");
  const [permissionSaving, setPermissionSaving] = useState(false);
  const [permissionError, setPermissionError] = useState<string | null>(null);
  const [coordinatorEmails, setCoordinatorEmails] = useState<string[]>([]);
  const [coordinatorEmailsLoading, setCoordinatorEmailsLoading] = useState(false);
  const [coordinatorEmailsError, setCoordinatorEmailsError] = useState<string | null>(null);
  const [recreatingTemplateId, setRecreatingTemplateId] = useState<string | null>(null);
  const [actionMessage, setActionMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);
  const [renamingSheet, setRenamingSheet] = useState<ProposeGoogleSheet | null>(null);
  const [renameName, setRenameName] = useState("");
  const [renameSaving, setRenameSaving] = useState(false);
  const [renameError, setRenameError] = useState<string | null>(null);
  const permissionSheetId = permissionSheet?.id;

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    listProposeGoogleSheets({
      page,
      page_size: pageSize,
      ...(search ? { search } : {}),
    })
      .then((result) => {
        if (!active) return;
        setItems(result.items);
        setTotal(result.pagination.total);
      })
      .catch((requestError) => {
        if (active) setError(getProposeTemplateErrorMessage(requestError));
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [page, pageSize, refreshKey, search]);

  useEffect(() => {
    if (permissionSheetId === undefined) return;
    let active = true;
    setCoordinatorEmailsLoading(true);
    setCoordinatorEmailsError(null);
    listProposalCoordinatorEmails()
      .then((emails) => {
        if (active) setCoordinatorEmails(emails);
      })
      .catch((requestError) => {
        if (active) {
          setCoordinatorEmailsError(getProposeTemplateErrorMessage(requestError));
        }
      })
      .finally(() => {
        if (active) setCoordinatorEmailsLoading(false);
      });
    return () => {
      active = false;
    };
  }, [permissionSheetId]);

  const recreateTemplate = async (sheet: ProposeGoogleSheet) => {
    if (sheet.template_id === undefined) {
      setActionMessage({
        type: "error",
        text: "このGoogle Sheetsに紐づくTemplate IDがありません。",
      });
      return;
    }

    setRecreatingTemplateId(sheet.id);
    setActionMessage(null);
    try {
      const result = await syncProposeTemplateGoogleSheet(sheet.template_id);
      if (!result.success) {
        setActionMessage({ type: "error", text: result.detail });
        return;
      }
      const form = await getProposeTemplate(sheet.template_id);
      await onTemplateCreated(sheet.template_id, form);
      setActionMessage({ type: "success", text: result.detail });
    } catch (requestError) {
      setActionMessage({
        type: "error",
        text: getProposeTemplateErrorMessage(requestError),
      });
    } finally {
      setRecreatingTemplateId(null);
    }
  };

  const saveSheetName = async () => {
    if (!renamingSheet?.template_id) return;
    const name = renameName.trim();
    if (!name) {
      setRenameError("テンプレート名を入力してください。");
      return;
    }

    setRenameSaving(true);
    setRenameError(null);
    try {
      const result = await renameProposeTemplate(renamingSheet.template_id, name);
      if (!result.success) {
        setRenameError(result.detail || "名前を変更できませんでした。");
        return;
      }
      setRenamingSheet(null);
      setActionMessage({ type: "success", text: result.detail || "Template name updated." });
      onTemplateRenamed(renamingSheet.template_id, name, result.detail);
    } catch (requestError) {
      setRenameError(getProposeTemplateErrorMessage(requestError));
    } finally {
      setRenameSaving(false);
    }
  };

  const columns: ColumnsType<ProposeGoogleSheet> = [
    {
      title: "シート名",
      dataIndex: "name",
      render: (name: string, sheet) => (
        <Space>
          <Typography.Link href={sheet.url} target="_blank" rel="noreferrer">
            {name}
          </Typography.Link>
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            aria-label={`${name}の名前を変更`}
            title={sheet.template_id === undefined ? "Template IDがありません" : undefined}
            disabled={sheet.template_id === undefined}
            onClick={() => {
              setRenamingSheet(sheet);
              setRenameName(name);
              setRenameError(null);
            }}
          />
        </Space>
      ),
    },
    {
      title: "Template ID",
      dataIndex: "template_id",
      render: (value?: number) => value ?? "—",
    },
    {
      title: "更新日時",
      dataIndex: "updatedAt",
      render: (value?: string) => formatDate(value),
    },
    {
      title: "作成日時",
      dataIndex: "createdAt",
      render: (value?: string) => formatDate(value),
    },
    {
      title: "操作",
      key: "permissions",
      render: (_, sheet) => (
        <Space wrap>
          <Button
            onClick={() => {
              setPermissionSheet(sheet);
              setPermissionEmails([]);
              const role = sheet.permissions?.[0]?.role;
              setPermissionRole(
                role === "view" || role === "comment" || role === "edit"
                  ? role
                  : "edit",
              );
              setPermissionError(null);
            }}
          >
            Permission
          </Button>
          <Button
            loading={recreatingTemplateId === sheet.id}
            disabled={
              sheet.template_id === undefined ||
              (recreatingTemplateId !== null && recreatingTemplateId !== sheet.id)
            }
            title={sheet.template_id === undefined ? "Template IDがありません" : undefined}
            onClick={() => void recreateTemplate(sheet)}
          >
            Re-Create Template
          </Button>
        </Space>
      ),
    },
  ];

  const permissionColumns: ColumnsType<ProposeGoogleSheetPermission> = [
    { title: "メールアドレス", dataIndex: "email", key: "email" },
    { title: "Role", dataIndex: "role", key: "role" },
  ];

  const savePermissions = async () => {
    if (!permissionSheet) return;
    const emails = [...new Set(permissionEmails.map((email) => email.trim()).filter(Boolean))];
    if (!emails.length || emails.some((email) => !emailPattern.test(email))) {
      setPermissionError("メールアドレスを正しく入力してください。");
      return;
    }
    setPermissionSaving(true);
    setPermissionError(null);
    try {
      const result = await updateProposeGoogleSheetPermissions(permissionSheet.id, {
        emails,
        role: permissionRole,
      });
      if (!result.success) {
        setPermissionError(result.detail || "権限を更新できませんでした。");
        return;
      }
      setPermissionSheet(null);
      setActionMessage({
        type: "success",
        text: result.detail || "Permissions updated.",
      });
      onUpdated();
    } catch (requestError) {
      setPermissionError(getProposeTemplateErrorMessage(requestError));
    } finally {
      setPermissionSaving(false);
    }
  };

  return (
    <Card
      title="Google Sheets一覧"
      bordered={false}
      style={{ marginTop: 20 }}
    >
      <Space.Compact style={{ width: "100%", marginBottom: 16 }}>
        <Input
          allowClear
          value={searchInput}
          placeholder="シート名で検索"
          onChange={(event) => setSearchInput(event.target.value)}
          onPressEnter={() => {
            setPage(1);
            setSearch(searchInput.trim());
          }}
        />
        <Button
          onClick={() => {
            setPage(1);
            setSearch(searchInput.trim());
          }}
        >
          検索
        </Button>
      </Space.Compact>
      {actionMessage && (
        <Alert
          type={actionMessage.type}
          showIcon
          message={actionMessage.text}
          closable
          onClose={() => setActionMessage(null)}
          style={{ marginBottom: 16 }}
        />
      )}
      {error && (
        <Alert type="error" showIcon message={error} style={{ marginBottom: 16 }} />
      )}
      <Table<ProposeGoogleSheet>
        rowKey="id"
        columns={columns}
        dataSource={items}
        loading={loading}
        pagination={false}
        scroll={{ x: 800 }}
        locale={{ emptyText: "Google Sheetsはありません" }}
      />
      <Pagination
        current={page}
        pageSize={pageSize}
        total={total}
        showSizeChanger
        pageSizeOptions={[20, 50, 100]}
        showTotal={(count, range) => `${count} 件中 ${range[0]}-${range[1]} を表示`}
        onChange={(nextPage, nextPageSize) => {
          setPage(nextPageSize !== pageSize ? 1 : nextPage);
          setPageSize(nextPageSize);
        }}
        style={{ marginTop: 20, textAlign: "right" }}
      />
      <Modal
        title={`テンプレート名を変更：${renamingSheet?.name ?? ""}`}
        open={renamingSheet !== null}
        onCancel={() => setRenamingSheet(null)}
        onOk={() => void saveSheetName()}
        okText="保存"
        cancelText="キャンセル"
        confirmLoading={renameSaving}
        destroyOnClose
      >
        <Space direction="vertical" size="small" style={{ width: "100%" }}>
          <label htmlFor="google-sheet-rename">Google Sheets名</label>
          <Input
            id="google-sheet-rename"
            autoFocus
            maxLength={200}
            value={renameName}
            onChange={(event) => setRenameName(event.target.value)}
            onPressEnter={() => void saveSheetName()}
            placeholder="テンプレート名を入力"
          />
          {renameError && <Alert type="error" showIcon message={renameError} />}
        </Space>
      </Modal>
      <Modal
        title={`Permission：${permissionSheet?.name ?? ""}`}
        open={permissionSheet !== null}
        onCancel={() => setPermissionSheet(null)}
        onOk={() => void savePermissions()}
        okText="保存"
        cancelText="キャンセル"
        confirmLoading={permissionSaving}
        destroyOnClose
      >
        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
          <Text strong>現在の権限</Text>
          <Table<ProposeGoogleSheetPermission>
            size="small"
            rowKey={(permission) => `${permission.email}:${permission.role}`}
            columns={permissionColumns}
            dataSource={permissionSheet?.permissions ?? []}
            pagination={false}
            locale={{ emptyText: "権限はありません" }}
          />
          <label htmlFor="google-sheet-permission-emails">メールアドレス</label>
          <Select
            id="google-sheet-permission-emails"
            mode="multiple"
            value={permissionEmails}
            loading={coordinatorEmailsLoading}
            options={coordinatorEmails.map((email) => ({
              value: email,
              label: email,
            }))}
            placeholder="メールアドレスを選択"
            onChange={setPermissionEmails}
            style={{ width: "100%" }}
          />
          {coordinatorEmailsError && (
            <Alert type="error" showIcon message={coordinatorEmailsError} />
          )}
          <label htmlFor="google-sheet-permission-role">Role</label>
          <Select<ProposeGoogleSheetRole>
            id="google-sheet-permission-role"
            value={permissionRole}
            options={[
              { value: "edit", label: "edit" },
              { value: "view", label: "view" },
              { value: "comment", label: "comment" },
            ]}
            onChange={setPermissionRole}
            style={{ width: "100%" }}
          />
          {permissionError && <Alert type="error" showIcon message={permissionError} />}
        </Space>
      </Modal>
    </Card>
  );
};
